/**
 * MH-15 Kho âm thanh (mục 5.5 đặc tả giao diện).
 *
 * Quản lý tệp dùng lại (nhạc hiệu, nhạc kết, cảnh báo...). Tệp đang được tham
 * chiếu KHÔNG xóa được; giao diện nêu rõ số bản tin đang dùng trước khi để
 * người dùng bấm xóa, tránh gọi API rồi mới báo lỗi.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { dienVao, vi } from '../ngon-ngu/vi';
import { khoAmThanhApi, type BoLocAudioAsset, type DongAudioAsset } from '../api/kho-am-thanh-api';
import type { TrangDuLieu } from '../api/thiet-bi-api';
import { dungHoan, dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { TrinhPhatAmThanh } from '../thanh-phan/TrinhPhatAmThanh';

const DINH_DANG_CHO_PHEP = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav'];

export interface MH15Props {
  api?: Pick<typeof khoAmThanhApi, 'danhSach' | 'soThamChieu' | 'xoa' | 'taiLen'>;
}

function coChu(n: number | null): string {
  if (n === null) return vi.banTin.chuaCo;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function MH15KhoAmThanh({ api }: MH15Props = {}): ReactNode {
  const goi = api ?? khoAmThanhApi;
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState(SO_DONG_MAC_DINH);
  const [oTim, datOTim] = useState('');
  const tuKhoaHoan = dungHoan(oTim, 400);
  const [nghe, datNghe] = useState<DongAudioAsset | null>(null);
  const [dangXoaId, datDangXoaId] = useState<string | null>(null);
  const [thongBaoXoa, datThongBaoXoa] = useState<string | null>(null);
  const [dangTaiLen, datDangTaiLen] = useState(false);
  const [loiTaiLen, datLoiTaiLen] = useState<string | null>(null);

  const dangLoc = tuKhoaHoan.trim().length >= 2;

  const kq = dungTaiDuLieu<TrangDuLieu<DongAudioAsset>>({
    tai: (signal) =>
      goi.danhSach(
        { page: trang, pageSize: soDong, keyword: dangLoc ? tuKhoaHoan.trim() : undefined } satisfies BoLocAudioAsset,
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [trang, soDong, tuKhoaHoan],
  });

  const xoa = useCallback(
    async (a: DongAudioAsset): Promise<void> => {
      datDangXoaId(a.id);
      datThongBaoXoa(null);
      try {
        const { soThamChieu } = await goi.soThamChieu(a.id);
        if (soThamChieu > 0) {
          datThongBaoXoa(dienVao(vi.khoAmThanh.khongXoaDuocDangDung, { so: soThamChieu }));
          return;
        }
        await goi.xoa(a.id);
        kq.taiLai();
      } finally {
        datDangXoaId(null);
      }
    },
    [goi, kq],
  );

  const taiLenMoi = useCallback(
    async (tep: File): Promise<void> => {
      if (!DINH_DANG_CHO_PHEP.includes(tep.type)) {
        datLoiTaiLen(vi.khoAmThanh.dinhDangSai);
        return;
      }
      datDangTaiLen(true);
      datLoiTaiLen(null);
      try {
        await goi.taiLen(tep, { ten: tep.name });
        kq.taiLai();
      } catch (e: unknown) {
        datLoiTaiLen(e instanceof Error ? e.message : 'Không tải lên được');
      } finally {
        datDangTaiLen(false);
      }
    },
    [goi, kq],
  );

  const cot: CotBang<DongAudioAsset>[] = [
    { khoa: 'ten', nhan: vi.khoAmThanh.cot.ten, ve: (d) => <span>{d.ten}</span> },
    { khoa: 'nhom', nhan: vi.khoAmThanh.cot.nhom, ve: (d) => <span>{d.nhom ?? vi.banTin.chuaCo}</span> },
    {
      khoa: 'thoiLuong',
      nhan: vi.khoAmThanh.cot.thoiLuong,
      ve: (d) => <span>{d.thoiLuongGiay ? `${d.thoiLuongGiay}s` : vi.banTin.chuaCo}</span>,
    },
    { khoa: 'kichThuoc', nhan: vi.khoAmThanh.cot.kichThuoc, ve: (d) => <span>{coChu(d.kichThuocByte)}</span> },
    { khoa: 'soThamChieu', nhan: vi.khoAmThanh.cot.soThamChieu, ve: (d) => <span>{d.soThamChieu}</span> },
    {
      khoa: 'hanhDong',
      nhan: vi.khoAmThanh.cot.hanhDong,
      ve: (d) => (
        <div className="bang__hanh-dong">
          <button type="button" className="nut nut--lien-ket" onClick={() => datNghe(d)}>
            {vi.khoAmThanh.ngheThu}
          </button>
          <button
            type="button"
            className="nut nut--lien-ket"
            disabled={dangXoaId === d.id}
            onClick={() => void xoa(d)}
          >
            {vi.khoAmThanh.xoa}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="man-hinh" data-man-hinh="MH-15">
      <div className="man-hinh__dau">
        <h1>{vi.khoAmThanh.tieuDe}</h1>
        <div className="man-hinh__hanh-dong">
          <label htmlFor="kat-tai-len" className="nut nut--chinh">
            {dangTaiLen ? '…' : vi.khoAmThanh.taiLenMoi}
          </label>
          <input
            id="kat-tai-len"
            type="file"
            accept="audio/mpeg,audio/wav,.mp3,.wav"
            className="chi-cho-doc-man-hinh"
            disabled={dangTaiLen}
            onChange={(e) => {
              const tep = e.target.files?.[0];
              if (tep) void taiLenMoi(tep);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {loiTaiLen ? (
        <p role="alert" data-vung="loi-tai-len">
          {loiTaiLen}
        </p>
      ) : null}
      {thongBaoXoa ? (
        <p role="alert" data-vung="thong-bao-xoa">
          {thongBaoXoa}
        </p>
      ) : null}

      <div className="man-hinh__loc" role="search">
        <label htmlFor="kat-tim">{vi.khoAmThanh.timKiem}</label>
        <input
          id="kat-tim"
          type="search"
          value={oTim}
          onChange={(e) => {
            datOTim(e.target.value);
            datTrang(1);
          }}
        />
      </div>

      {nghe ? (
        <div data-vung="nghe-thu-kho">
          <TrinhPhatAmThanh nhan={dienVao('Nghe thử {ten}', { ten: nghe.ten })} url={nghe.urlTaiVe} />
        </div>
      ) : null}

      <BangDuLieu<DongAudioAsset>
        nhan={vi.khoAmThanh.tieuDe}
        cot={cot}
        dong={kq.duLieu?.items ?? []}
        khoaDong={(d) => d.id}
        nhanDong={(d) => d.ten}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={kq.taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={datTrang}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
        }}
        dangLoc={dangLoc}
        onXoaLoc={() => {
          datOTim('');
          datTrang(1);
        }}
        moTaRong={vi.khoAmThanh.rongMoTa}
      />
    </div>
  );
}
